import { z } from "zod";
import { uuid } from "./common";

/* Data import (CSV) and background exports (Phase 8d). */

/** Patient fields a CSV column can be mapped to. */
export const PATIENT_IMPORT_FIELDS = [
  "fullName", "phone", "whatsappPhone", "email", "gender", "dateOfBirth", "addressLine1", "addressLine2", "city", "region", "postalCode",
  "occupation", "preferredLanguage", "notes",
] as const;
export type PatientImportField = (typeof PATIENT_IMPORT_FIELDS)[number];
export const PATIENT_IMPORT_LABELS: Record<PatientImportField, string> = {
  fullName: "Full name", phone: "Phone", whatsappPhone: "WhatsApp number", email: "Email", gender: "Gender", dateOfBirth: "Date of birth",
  addressLine1: "Address line 1", addressLine2: "Address line 2", city: "City", region: "State", postalCode: "PIN code", occupation: "Occupation",
  preferredLanguage: "Language", notes: "Notes",
};

/** Up to about 5 MB of CSV text (≈ 20,000 patients). */
export const importCreateInput = z.object({
  kind: z.literal("patients"),
  filename: z.string().trim().min(1).max(200),
  csv: z.string().min(1).max(5_000_000),
});
export const importMappingInput = z.object({
  /** target field → source column header (missing or null = not imported). */
  mapping: z.partialRecord(z.enum(PATIENT_IMPORT_FIELDS), z.string().max(200).nullable()),
  options: z.object({
    /** Skip rows whose phone already belongs to a patient (otherwise they are reported as errors). */
    skipExisting: z.boolean().default(true),
    /** dd/mm/yyyy (India) or mm/dd/yyyy for slashed dates. */
    dateOrder: z.enum(["dmy", "mdy"]).default("dmy"),
  }),
});

export interface ImportJobView {
  id: string;
  kind: "patients";
  filename: string;
  headers: string[];
  mapping: Partial<Record<PatientImportField, string | null>>;
  status: "uploaded" | "mapping" | "validating" | "validated" | "importing" | "completed" | "failed" | "cancelled";
  totalRows: number;
  validRows: number;
  invalidRows: number;
  skippedRows: number;
  importedRows: number;
  lastError: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** First rows of the file, to help with the mapping (only while the job is open). */
  sample: string[][];
}
export interface ImportRowIssue {
  rowNumber: number;
  status: "invalid" | "skipped";
  errors: { field: string; message: string }[];
}

export const EXPORT_KINDS = ["patients", "appointments", "invoices", "payments", "patient_data_request"] as const;
export type ExportKind = (typeof EXPORT_KINDS)[number];
export const EXPORT_LABELS: Record<ExportKind, string> = {
  patients: "All patients (contact details)",
  appointments: "Appointments",
  invoices: "Invoices",
  payments: "Payments",
  patient_data_request: "Everything about one patient (data request)",
};
export const exportCreateInput = z.object({
  kind: z.enum(EXPORT_KINDS),
  reason: z.string().trim().min(3).max(500),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  patientId: uuid.optional(),
});
export interface ExportJobView {
  id: string;
  kind: ExportKind;
  status: "queued" | "running" | "completed" | "failed" | "expired" | "cancelled";
  reason: string;
  filters: { from?: string; to?: string; patientId?: string };
  rowCount: number | null;
  filename: string | null;
  expiresAt: string | null;
  downloadCount: number;
  requestedBy: string;
  lastError: string | null;
  createdAt: string;
}
